/**
 * assets/custom-pricing.js  v2
 *
 * Formula-driven dynamic pricing for Shopify (no third-party apps).
 * Compatible with OS 2.0 / Dawn themes AND legacy jQuery-AJAX themes (Gecko, etc.).
 *
 * FORMULA ENGINE
 * ==============
 * Reads a JSON formula stored in the product metafield `custom.pricing_formula`.
 * The formula is a cumulative-band model that exactly mirrors Excel IFS/IF formulas:
 *
 *   Segment 0 : rate applied to ALL units up to qty_max, plus an entry fee
 *   Segment 1+: rate applied to units ABOVE previous segment's qty_max, plus entry fee
 *
 * Example — client's Excel formula translated:
 *   OR(W<2, L<2, Q<200)   → "Out of Range"
 *   Q<=250  → (W*L*0.046*Q)       + 25
 *   Q<=500  → (seg0 cost)         + (W*L*0.035*(Q-250)) + 15
 *   Q<=1000 → (seg0+1 cost)       + (W*L*0.027*(Q-500)) + 20   (20 = excel's 35 − 15)
 *   Q<=2000 → (seg0+1+2 cost)     + (W*L*0.015*(Q-1000))
 *   else    → "Out of Range"
 *
 * JSON stored in metafield (editable by merchant per product, no code change):
 * {
 *   "mode": "sqft_qty",          // "sqft_qty" | "sqft_only" | "qty_only"
 *   "min_width": 2,
 *   "min_length": 2,
 *   "min_qty": 200,
 *   "max_qty": 2000,
 *   "segments": [
 *     { "qty_max": 250,  "rate": 0.046, "fee": 25 },
 *     { "qty_max": 500,  "rate": 0.035, "fee": 15 },
 *     { "qty_max": 1000, "rate": 0.027, "fee": 20 },
 *     { "qty_max": 2000, "rate": 0.015, "fee": 0  }
 *   ]
 * }
 *
 * CART TRICK
 * ==========
 * Shopify prevents arbitrary price changes from the storefront.
 * The workaround: product variant is priced at $1.00 (unit_value).
 * cart quantity submitted = round(total_price / unit_value)
 * so Shopify line total = $1.00 × 47 = $47.00 (the real price).
 *
 * CART DISPLAY
 * ============
 * The cart page will show "Qty: 47" which looks wrong when the customer ordered 1 banner.
 * This file also provides fixCartDisplay() which rewrites that using line item properties.
 * Hook it into your cart-refresh events, or call window.CustomPricingCartFix().
 *
 * THEME COMPATIBILITY
 * ===================
 * Strategy 1 – native form submit (Dawn / OS 2.0)
 * Strategy 2 – button click capture before jQuery handler (Gecko / legacy themes)
 * Strategy 3 – $.ajax intercept as a safety net (Gecko edge cases)
 */

(function () {
  'use strict';

  const SELECTOR  = '[data-custom-pricing]';
  const DEBOUNCE  = 150; // ms

  // ─── Helpers ───────────────────────────────────────────────────────────────

  function debounce(fn, ms) {
    let t;
    return function (...a) { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), ms); };
  }

  function fmt(amount, symbol) {
    return `${symbol}${(isFinite(amount) ? amount : 0).toFixed(2)}`;
  }

  // ─── Formula engine ────────────────────────────────────────────────────────

  /**
   * Evaluates the cumulative-band formula against width, length, and qty.
   * Returns { total, area, tier, rate, breakdown } on success,
   * or { error: string } on out-of-range / invalid input.
   *
   * This precisely mirrors the client's Excel IFS formula.
   */
  function evaluate(formula, width, length, qty) {
    const {
      mode        = 'sqft_qty',
      min_width   = 0,
      min_length  = 0,
      min_qty     = 1,
      max_qty,
      segments    = [],
    } = formula;

    // ── Validation ────────────────────────────────────────────────────────────
    if ((mode === 'sqft_qty' || mode === 'sqft_only') && width < min_width)
      return { error: `Width must be at least ${min_width} ft` };
    if ((mode === 'sqft_qty' || mode === 'sqft_only') && length < min_length)
      return { error: `Length must be at least ${min_length} ft` };
    if ((mode === 'sqft_qty' || mode === 'qty_only') && qty < min_qty)
      return { error: `Minimum quantity is ${min_qty}` };
    if (max_qty && (mode === 'sqft_qty' || mode === 'qty_only') && qty > max_qty)
      return { error: `Maximum quantity is ${max_qty}` };
    if (segments.length === 0)
      return { error: 'No pricing formula configured for this product' };

    // ── Area ─────────────────────────────────────────────────────────────────
    const area = (mode === 'sqft_qty' || mode === 'sqft_only')
      ? Math.round(width * length * 100) / 100  // 2-decimal area
      : null;

    // ── Cumulative band calculation ───────────────────────────────────────────
    // segments[0]: rate applies to ALL qty up to its qty_max.
    // segments[1+]: rate applies only to qty ABOVE previous segment's qty_max.
    // Each segment adds its fee when the order quantity enters that band.
    let total        = 0;
    let activeRate   = segments[0].rate;
    let activeTier   = segments[0];
    let prevMax      = 0;

    for (let i = 0; i < segments.length; i++) {
      const seg       = segments[i];
      const bandMax   = seg.qty_max;
      // For sqft_only, qty isn't a dimension — we use area bands instead
      const measure   = (mode === 'sqft_only') ? area : qty;
      const bandValue = (i === 0)
        ? Math.min(measure, bandMax)          // first band: all units up to its max
        : Math.min(measure, bandMax) - prevMax; // subsequent: only units above prev max

      if (bandValue <= 0) { prevMax = bandMax; continue; }

      const unitFactor = (mode === 'qty_only')
        ? 1                  // simple per-item price
        : area;              // sqft modes: multiply by area

      total      += unitFactor * seg.rate * bandValue + seg.fee;
      activeRate  = seg.rate;
      activeTier  = seg;
      prevMax     = bandMax;

      if (measure <= bandMax) break; // we've consumed all quantity in this segment
    }

    return { total, area, rate: activeRate, tier: activeTier };
  }

  // ─── Main class ────────────────────────────────────────────────────────────

  class CustomPricing {
    constructor(root) {
      this.root       = root;
      this.mode       = root.dataset.pricingMode;
      this.unitValue  = parseFloat(root.dataset.unitValue) || 1.0;
      this.symbol     = this._detectSymbol();
      this.formula    = this._parseFormula(root);
      this.lastResult = null;
      this.form       = root.closest('form');

      this.els = {
        width:      root.querySelector('[data-pricing-input="width"]'),
        length:     root.querySelector('[data-pricing-input="length"]'),
        realQty:    root.querySelector('[data-pricing-input="real_quantity"]'),
        qtyDec:     root.querySelector('[data-pricing-qty-decrement]'),
        qtyInc:     root.querySelector('[data-pricing-qty-increment]'),
        outArea:    root.querySelector('[data-pricing-output="area"]'),
        outRate:    root.querySelector('[data-pricing-output="rate"]'),
        outTotal:   root.querySelector('[data-pricing-output="total"]'),
        error:      root.querySelector('[data-pricing-error]'),
        cartQty:    root.querySelector('[data-pricing-cart-quantity]'),
        props: {
          width:       root.querySelector('[data-pricing-prop="width"]'),
          length:      root.querySelector('[data-pricing-prop="length"]'),
          area:        root.querySelector('[data-pricing-prop="area"]'),
          tier_label:  root.querySelector('[data-pricing-prop="tier_label"]'),
          rate_label:  root.querySelector('[data-pricing-prop="rate_label"]'),
          real_qty:    root.querySelector('[data-pricing-prop="real_quantity_label"]'),
          total_label: root.querySelector('[data-pricing-prop="total_label"]'),
        },
      };

      // Expose instance on DOM node so the $.ajax patch can reach it
      root._cpInstance = this;

      this._bindEvents();
      this.recalculate();
    }

    _parseFormula(root) {
      const el = root.querySelector('[data-pricing-formula]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error('[custom-pricing] Could not parse formula JSON:', e);
        return null;
      }
    }

    _detectSymbol() {
      const el = document.querySelector(
        '[data-product-price], .price-item, .productPrice, .price'
      );
      if (el) {
        const m = el.textContent.match(/^[^\d.,\s]+/);
        if (m) return m[0];
      }
      return '$';
    }

    _bindEvents() {
      const recalc = debounce(() => this.recalculate(), DEBOUNCE);

      this.els.width   && this.els.width.addEventListener('input', recalc);
      this.els.length  && this.els.length.addEventListener('input', recalc);
      this.els.realQty && this.els.realQty.addEventListener('input', recalc);

      // +/- stepper buttons
      this.els.qtyDec && this.els.qtyDec.addEventListener('click', () => {
        const min = parseInt(this.els.realQty.min, 10) || 1;
        const cur = parseInt(this.els.realQty.value, 10) || min;
        this.els.realQty.value = Math.max(min, cur - 1);
        this.recalculate();
      });
      this.els.qtyInc && this.els.qtyInc.addEventListener('click', () => {
        const min = parseInt(this.els.realQty.min, 10) || 1;
        const cur = parseInt(this.els.realQty.value, 10) || min;
        this.els.realQty.value = cur + 1;
        this.recalculate();
      });

      if (!this.form) return;

      // Strategy 1: OS 2.0 / Dawn — native form submit
      this.form.addEventListener('submit',
        (e) => this._beforeCartAdd(e, 'submit'), { capture: true });

      // Strategy 2: Gecko / legacy jQuery-AJAX — intercept button click
      // before jQuery's delegated handler reads the form data
      const btn = this.form.querySelector(
        '#shopify_add_to_cart, button[type="submit"], input[type="submit"]'
      );
      btn && btn.addEventListener('click',
        (e) => this._beforeCartAdd(e, 'click'), { capture: true });

      // Strategy 3: $.ajax patch — deepest hook, catches anything that slips past #2
      this._patchjQuery();
    }

    /**
     * Patches window.jQuery.ajax once per page. Intercepts /cart/add calls,
     * rewrites the POST body with the correct multiplier quantity and properties.
     * This is the safety net for Gecko's internal AJAX cart handler.
     */
    _patchjQuery() {
      if (!window.jQuery || window._cpAjaxPatched) return;
      window._cpAjaxPatched = true;

      const _orig = window.jQuery.ajax.bind(window.jQuery);
      window.jQuery.ajax = function (urlOrOpts, opts) {
        if (typeof urlOrOpts === 'object') { opts = urlOrOpts; urlOrOpts = undefined; }
        opts = opts || {};
        const url = urlOrOpts || opts.url || '';

        if (/\/cart\/add/.test(url)) {
          const el  = document.querySelector('[data-custom-pricing][data-pricing-initialized]');
          const ins = el && el._cpInstance;
          if (ins) {
            ins.recalculate();
            const r = ins.lastResult;

            if (!r || r.error || r.total <= 0) {
              ins.showError(r && r.error ? r.error : 'Please fill in all dimensions first.');
              const d = window.jQuery.Deferred();
              setTimeout(() => d.reject({ responseText: 'Custom pricing validation failed.' }), 0);
              return d.promise();
            }

            ins._applyToHiddenFields(r);

            if (typeof opts.data === 'string') {
              opts.data = ins._rewritePostData(opts.data, r);
            } else if (opts.data && typeof opts.data === 'object') {
              opts.data.quantity = Math.max(1, Math.round(r.total / ins.unitValue));
            }

            if (urlOrOpts) opts.url = url;
          }
        }

        return urlOrOpts !== undefined
          ? _orig(urlOrOpts, opts)
          : _orig(opts);
      };
    }

    // ── Calculation ──────────────────────────────────────────────────────────

    recalculate() {
      this.clearError();
      if (!this.formula) {
        this.showError('No pricing formula found on this product.');
        return;
      }

      const width  = parseFloat(this.els.width?.value)   || 0;
      const length = parseFloat(this.els.length?.value)  || 0;
      const qty    = parseInt(this.els.realQty?.value, 10) || 0;

      // Don't show an error on empty fields — just leave the summary blank
      const needsW = this.mode === 'sqft_qty' || this.mode === 'sqft_only';
      const needsQ = this.mode === 'sqft_qty' || this.mode === 'qty_only';

      if ((needsW && (width <= 0 || length <= 0)) || (needsQ && qty <= 0)) {
        this._renderEmpty();
        return;
      }

      const result = evaluate(this.formula, width, length, qty);

      if (result.error) {
        this.showError(result.error);
        this._renderEmpty();
        this.lastResult = null;
        return;
      }

      result.width = width;
      result.length = length;
      result.qty = qty;
      this.lastResult = result;
      this._render(result);
    }

    _render(r) {
      if (this.els.outArea && r.area !== null) {
        this.els.outArea.textContent = `${r.area.toFixed(2)} sqft`;
      }
      if (this.els.outRate) {
        this.els.outRate.textContent = this.mode === 'qty_only'
          ? `${fmt(r.rate, this.symbol)} / item`
          : `${fmt(r.rate, this.symbol)} / sqft / unit`;
      }
      if (this.els.outTotal) {
        this.els.outTotal.textContent = fmt(r.total, this.symbol);
      }
    }

    _renderEmpty() {
      if (this.els.outArea)  this.els.outArea.textContent  = '—';
      if (this.els.outRate)  this.els.outRate.textContent  = '—';
      if (this.els.outTotal) this.els.outTotal.textContent = '—';
    }

    showError(msg) {
      if (this.els.error) { this.els.error.textContent = msg; this.els.error.hidden = false; }
    }
    clearError() {
      if (this.els.error) { this.els.error.textContent = ''; this.els.error.hidden = true; }
    }

    // ── Cart interception ────────────────────────────────────────────────────

    _beforeCartAdd(e, trigger) {
      this.recalculate();
      const r = this.lastResult;

      if (!r || r.error || r.total <= 0) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showError(r?.error || 'Please fill in all required fields before adding to cart.');
        return;
      }

      this._applyToHiddenFields(r);
    }

    _applyToHiddenFields(r) {
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      if (this.els.cartQty) this.els.cartQty.value = cartQty;

      const tierLabel = r.tier
        ? `${r.tier.qty_max >= 999999 ? 'Open-ended' : 'Up to ' + r.tier.qty_max} (rate: ${r.rate})`
        : '';

      const p = this.els.props;
      if (p.width && r.width != null)   p.width.value   = r.width.toFixed(2);
      if (p.length && r.length != null) p.length.value  = r.length.toFixed(2);
      if (p.area && r.area != null)     p.area.value    = r.area.toFixed(2);
      if (p.tier_label)    p.tier_label.value    = tierLabel;
      if (p.rate_label)    p.rate_label.value    = fmt(r.rate, this.symbol);
      if (p.real_qty)      p.real_qty.value      = r.qty ?? '';
      if (p.total_label)   p.total_label.value   = fmt(r.total, this.symbol);
    }

    _rewritePostData(str, r) {
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      const p = new URLSearchParams(str);
      p.set('quantity', cartQty);
      if (r.width  != null) p.set('properties[Width (ft)]',   r.width.toFixed(2));
      if (r.length != null) p.set('properties[Length (ft)]',  r.length.toFixed(2));
      if (r.area   != null) p.set('properties[Area (sqft)]',  r.area.toFixed(2));
      if (r.qty    != null) p.set('properties[Quantity Ordered]', r.qty);
      p.set('properties[Rate Applied]', fmt(r.rate, this.symbol));
      p.set('properties[Your Price]',   fmt(r.total, this.symbol));
      return p.toString();
    }
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.dataset.pricingInitialized) {
        el.dataset.pricingInitialized = 'true';
        new CustomPricing(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('cart:refresh', () => initAll());
  window.CustomPricingInit = initAll;

})();

// ─────────────────────────────────────────────────────────────────────────────
// CART DISPLAY FIX
// ─────────────────────────────────────────────────────────────────────────────
// The $1-unit-price trick makes the cart show e.g. "Qty: 47" when the customer
// ordered 1 banner at $47. This function rewrites the cart item display to show:
//   - the REAL quantity from the "Quantity Ordered" line item property
//   - the REAL price from the "Your Price" line item property
//
// Call this after every cart render. Hook examples:
//   document.addEventListener('cart:refresh', () => window.CustomPricingCartFix());
//   // Gecko themes emit 'jas_cart_updated' or re-render the drawer via AJAX;
//   // patch that callback or use a MutationObserver as shown below.
//
// IMPORTANT: You also need a small edit to your cart item Liquid template.
// Add data-cp-line to each cart line wrapper, and data-cp-qty / data-cp-price
// to the quantity and price elements you want overridden.
// See snippets/custom-pricing-cart-item.liquid for the recommended template addition.

(function () {
  function fixCartDisplay() {
    // Find every cart line that has custom pricing properties attached
    document.querySelectorAll('[data-cp-line]').forEach((line) => {
      const props  = {};
      line.querySelectorAll('[data-cp-prop]').forEach((el) => {
        props[el.dataset.cpProp] = el.textContent.trim();
      });

      if (!props['Your Price']) return; // not a custom-priced item

      // Rewrite visible quantity with the real ordered quantity
      const qtyEl = line.querySelector('[data-cp-qty]');
      if (qtyEl && props['Quantity Ordered']) {
        qtyEl.textContent = props['Quantity Ordered'];
      }

      // Rewrite visible price with the calculated total
      const priceEl = line.querySelector('[data-cp-price]');
      if (priceEl) {
        priceEl.textContent = props['Your Price'];
      }

      // Hide the raw "$1.00 × N" sub-price Shopify normally renders
      const unitPriceEl = line.querySelector('[data-cp-unit-price]');
      if (unitPriceEl) unitPriceEl.style.display = 'none';
    });
  }

  // Run on DOMContentLoaded and any time the cart refreshes
  document.addEventListener('DOMContentLoaded', fixCartDisplay);
  document.addEventListener('cart:refresh', fixCartDisplay);

  // MutationObserver for themes that inject cart HTML via JS (Gecko cart drawer)
  const cartContainers = ['#shopify-cart', '.jas_mini_cart', '[data-cart-drawer]',
                           '#mini-cart', '.cart-drawer', '#CartDrawer'];
  cartContainers.forEach((sel) => {
    const el = document.querySelector(sel);
    if (!el) return;
    new MutationObserver(fixCartDisplay).observe(el, { childList: true, subtree: true });
  });

  window.CustomPricingCartFix = fixCartDisplay;
})();


// ─────────────────────────────────────────────────────────────────────────────
// LABEL MATRIX FORMULA ENGINE  v2
// Matches client's Google Sheets formula exactly.
//
// Key differences from v1:
//  - Tries BOTH label orientations (width-across vs length-across), picks
//    whichever uses fewer linear feet of roll material (min feet)
//  - Row gap only (0.125") — no column gap
//  - Uses ceil() for linear feet calculation (matching Google Sheets CEILING)
//  - Updated material costs: Vinyl $0.44/ft, Paper $0.27/ft
//  - Updated multiplier table (higher tiers: 1.9×, 1.8×, 1.7×, 1.6×)
//  - Two separate fee rules:
//      1. material_cost < $20  → add $25 (small label surcharge)
//      2. price_tier_calc < $175 → add $25 (low order surcharge)
//  - Min size 1"×1"
//  - If label area < 4 sq inches → minimum qty bumps to 500
//  - Absolute price floor: $55
// ─────────────────────────────────────────────────────────────────────────────

(function () {
  'use strict';

  /**
   * evaluateLabelMatrix(formula, width, length, qty, materialValue)
   *
   * All configuration numbers come from the metafield JSON.
   * JS implements the algorithm; merchants edit numbers, not code.
   *
   * Returns { total, rollFeet, upsAcross, materialCost, inkCost, rawCost, orientation }
   * or      { error: string }
   */
  function evaluateLabelMatrix(formula, width, length, qty, materialValue) {
    const { rules, production, materials, multipliers } = formula;

    // ── Validation ────────────────────────────────────────────────────────
    const minW = rules.min_width  || 1;
    const minL = rules.min_length || 1;
    if (width  < minW) return { error: `Minimum width is ${minW}"` };
    if (length < minL) return { error: `Minimum length is ${minL}"` };

    const area = width * length;

    // Small label rule: area < threshold → higher minimum qty
    const smallThreshold = rules.small_label_threshold_sqin || 4;
    const smallMinQty    = rules.small_label_min_qty        || 500;
    const effectiveMinQty = area < smallThreshold ? smallMinQty : (rules.min_qty || 250);

    if (!qty || qty < effectiveMinQty) {
      return { error: area < smallThreshold
        ? `Labels under ${smallThreshold} sq in require a minimum of ${smallMinQty}`
        : `Minimum quantity is ${effectiveMinQty}` };
    }
    if (qty > (rules.max_qty || 2500))
      return { error: `Maximum quantity is ${rules.max_qty || 2500}` };

    // ── Nesting: try both orientations, pick the one using less material ──
    // Orientation A: shorter dimension goes across the roll width
    const rollWidth = production.roll_width_inches || 7.5;
    const rowGap    = production.row_spacing_gap   || 0.125;

    const acrossA = Math.max(1, Math.floor(rollWidth / width));
    const rowsA   = Math.ceil(qty / acrossA);
    const feetA   = Math.ceil((rowsA * (length + rowGap)) / 12);

    // Orientation B: longer dimension goes across
    const acrossB = Math.max(1, Math.floor(rollWidth / length));
    const rowsB   = Math.ceil(qty / acrossB);
    const feetB   = Math.ceil((rowsB * (width + rowGap)) / 12);

    // Pick whichever uses less roll footage
    const useA        = feetA <= feetB;
    const rollFeet    = useA ? feetA : feetB;
    const upsAcross   = useA ? acrossA : acrossB;
    const orientation = useA ? 'A' : 'B';

    // ── Material cost ─────────────────────────────────────────────────────
    const matEntry  = (materials || []).find(m => m.value === materialValue);
    const costPerFt = matEntry ? (matEntry.cost_per_foot || 0) : 0;
    const materialCost = rollFeet * costPerFt;

    // ── Ink cost ──────────────────────────────────────────────────────────
    const inkRate = production.ink_cost_per_sqin || 0.002;
    const inkCost = qty * width * length * inkRate;

    // ── Raw cost ──────────────────────────────────────────────────────────
    const rawCost = materialCost + inkCost;

    // ── Markup multiplier ─────────────────────────────────────────────────
    let priceTier = 0;
    for (const tier of multipliers) {
      if (rawCost <= tier.max) {
        priceTier = rawCost * tier.mult + (tier.add || 0);
        break;
      }
    }

    // ── Fee rules ─────────────────────────────────────────────────────────
    // Rule 1: low order surcharge — price tier < $175 adds $25
    const lowOrderFee = priceTier < (rules.low_price_threshold || 175)
      ? (rules.low_price_fee || 25) : 0;

    // Rule 2: small material run — material cost < $20 adds $25
    const lowMaterialFee = materialCost < (rules.low_material_cost_threshold || 20)
      ? (rules.low_material_cost_fee || 25) : 0;

    // ── Final price ───────────────────────────────────────────────────────
    const total = Math.max(
      rules.minimum_order_price || 55,
      priceTier + lowOrderFee + lowMaterialFee
    );

    return { total, rollFeet, upsAcross, orientation, materialCost, inkCost, rawCost, priceTier };
  }

  // ── Hook into the existing CustomPricing class ───────────────────────────

  function patchLabelInstance(instance) {
    if (instance.mode !== 'label_matrix') return;

    instance.recalculate = function () {
      this.clearError();
      if (!this.formula) { this.showError('No pricing formula found.'); return; }

      const width    = parseFloat(this.els.width?.value)    || 0;
      const length   = parseFloat(this.els.length?.value)   || 0;
      const qty      = parseInt(this.els.realQty?.value, 10) || 0;
      const matEl    = this.root.querySelector('[data-pricing-input="material"]');
      const material = matEl ? matEl.value : 'Vinyl';

      if (!width || !length || !qty) { this._renderEmpty(); return; }

      const result = evaluateLabelMatrix(this.formula, width, length, qty, material);

      if (result.error) {
        this.showError(result.error);
        this._renderEmpty();
        this.lastResult = null;
        return;
      }

      result.width  = width;
      result.length = length;
      result.qty    = qty;
      result.mode   = 'label_matrix';
      this.lastResult = result;
      this._renderLabel(result);
    };

    instance._renderLabel = function (r) {
      if (this.els.outTotal) this.els.outTotal.textContent = `$${r.total.toFixed(2)}`;
      // Switch feedback box to green
    };

    // Override showError to turn the feedback box red
    const _origShowError = instance.showError.bind(instance);
    instance.showError = function (msg) {
      _origShowError(msg);
      if (this.mode !== 'label_matrix') return;
      const box = this.root.querySelector('[data-pricing-summary]');
      if (this.els.outTotal) this.els.outTotal.textContent = '----';
    };

    instance._applyToHiddenFields = function (r) {
      if (r.mode !== 'label_matrix') return;
      const cartQty = Math.max(1, Math.round(r.total / this.unitValue));
      if (this.els.cartQty) this.els.cartQty.value = cartQty;
      const p = this.els.props;
      if (p.ups_label)          p.ups_label.value          = `${r.upsAcross} (orientation ${r.orientation})`;
      if (p.linfeet_label)      p.linfeet_label.value      = `${r.rollFeet} ft`;
      if (p.real_quantity_label) p.real_quantity_label.value = r.qty;
      if (p.total_label)        p.total_label.value        = `$${r.total.toFixed(2)}`;
    };

    // Re-query label-specific props
    instance.els.props.ups_label     = instance.root.querySelector('[data-pricing-prop="ups_label"]');
    instance.els.props.linfeet_label = instance.root.querySelector('[data-pricing-prop="linfeet_label"]');

    // Listen to material/finish selects
    const matSel = instance.root.querySelector('[data-pricing-input="material"]');
    const finSel = instance.root.querySelector('[data-pricing-input="finish"]');
    if (matSel) matSel.addEventListener('change', () => instance.recalculate());
    if (finSel) finSel.addEventListener('change', () => instance.recalculate());

    instance.recalculate();
  }

  // Patch label instances after init
  const _origInit = window.CustomPricingInit;
  window.CustomPricingInit = function (root) {
    _origInit && _origInit(root);
    (root || document)
      .querySelectorAll('[data-custom-pricing][data-pricing-mode="label_matrix"]')
      .forEach(el => { if (el._cpInstance) patchLabelInstance(el._cpInstance); });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-custom-pricing][data-pricing-mode="label_matrix"]')
      .forEach(el => { if (el._cpInstance) patchLabelInstance(el._cpInstance); });
  });

})();

// ─────────────────────────────────────────────────────────────────────────────
// CART DISPLAY FIX
// ─────────────────────────────────────────────────────────────────────────────
(function () {
  function fixCartDisplay() {
    document.querySelectorAll('[data-cp-line]').forEach((line) => {
      const props = {};
      line.querySelectorAll('[data-cp-prop]').forEach(el => { props[el.dataset.cpProp] = el.textContent.trim(); });
      if (!props['Your Price']) return;
      const qtyEl   = line.querySelector('[data-cp-qty]');
      const priceEl = line.querySelector('[data-cp-price]');
      if (qtyEl   && props['Quantity Ordered']) qtyEl.textContent   = props['Quantity Ordered'];
      if (priceEl) priceEl.textContent = props['Your Price'];
      const unitPriceEl = line.querySelector('[data-cp-unit-price]');
      if (unitPriceEl) unitPriceEl.style.display = 'none';
    });
  }
  document.addEventListener('DOMContentLoaded', fixCartDisplay);
  document.addEventListener('cart:refresh', fixCartDisplay);
  const selectors = ['#shopify-cart','.jas_mini_cart','[data-cart-drawer]','#mini-cart','.cart-drawer','#CartDrawer'];
  selectors.forEach(sel => {
    const el = document.querySelector(sel);
    if (el) new MutationObserver(fixCartDisplay).observe(el, { childList: true, subtree: true });
  });
  window.CustomPricingCartFix = fixCartDisplay;
})();